import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import ShieldLogo from "./ShieldLogo";
import { scrollToId } from "@/lib/scroll";
import { api, getToken, setToken } from "@/lib/api";

const LINKS = [
    { label: "LIVE MAP", target: "#live-map" },
    { label: "FEATURES", target: "#features" },
    { label: "MANIFESTO", target: "#manifesto" },
    { label: "ROUTE SPECS", target: "#route-specs" },
    { label: "ALL FEATURES", route: "/features" },
];

const portalToast = (who) =>
    toast(who === "driver" ? "DRIVER AUTHORIZATION PORTAL" : "FLEET OPERATIONS PORTAL", {
        description: "Portal is staged and ready — production credentials coming soon. Keep the shiny side up.",
    });

export default function Navbar() {
    const navigate = useNavigate();
    // Token presence drives the auth buttons; re-check on tab focus so a
    // login/logout in another tab is reflected without a reload
    const [signedIn, setSignedIn] = useState(() => !!getToken());
    useEffect(() => {
        const check = () => setSignedIn(!!getToken());
        window.addEventListener("focus", check);
        return () => window.removeEventListener("focus", check);
    }, []);

    const signOut = async () => {
        try {
            await api("/auth/logout", { method: "POST" });
        } catch {}
        setToken(null);
        setSignedIn(false);
        toast.success("SIGNED OUT — KEEP THE SHINY SIDE UP");
        navigate("/");
    };

    return (
        <header
            data-testid="main-nav"
            className="fixed top-0 left-0 right-0 z-50 border-b-2 border-[#E5A93C]/30 bg-[#161413]/90 backdrop-blur-md"
        >
            <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-4 py-3 lg:px-8">
                <button
                    data-testid="nav-brand-logo"
                    onClick={() => window.__lenis?.scrollTo(0)}
                    className="flex items-center gap-3"
                >
                    <ShieldLogo size={40} />
                    <span className="hidden text-left leading-none sm:block">
                        <span className="font-display block text-xl tracking-widest text-[#F6F2E8]">
                            PUT CHA FOOT IN IT
                        </span>
                        <span className="font-mono2 block text-[10px] font-bold tracking-[0.35em] text-[#E5A93C]">
                            DRIVER!
                        </span>
                    </span>
                </button>

                <nav className="hidden items-center gap-8 lg:flex" data-testid="nav-links">
                    {LINKS.map((l) => (
                        <button
                            key={l.label}
                            data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                            onClick={() => (l.route ? navigate(l.route) : scrollToId(l.target))}
                            className="font-mono2 text-xs font-bold tracking-[0.25em] text-[#C2C7CE] transition-colors hover:text-[#E5A93C]"
                        >
                            {l.label}
                        </button>
                    ))}
                </nav>

                <div className="flex items-center gap-2 sm:gap-3">
                    {signedIn ? (
                        <>
                            <button
                                data-testid="nav-my-cab-btn"
                                onClick={() => navigate("/driver")}
                                className="btn-press font-display border-2 border-[#0E0D0C] bg-[#D32F2F] px-3 py-2 text-sm tracking-widest text-[#F6F2E8] shadow-[3px_3px_0px_#0E0D0C] sm:px-5 sm:text-base"
                            >
                                MY CAB
                            </button>
                            <button
                                data-testid="nav-sign-out-btn"
                                onClick={signOut}
                                className="btn-press font-display border-2 border-[#0E0D0C] bg-[#24211E] px-3 py-2 text-sm tracking-widest text-[#D32F2F] shadow-[3px_3px_0px_#0E0D0C] sm:px-5 sm:text-base"
                            >
                                SIGN OUT
                            </button>
                        </>
                    ) : (
                        <button
                            data-testid="nav-driver-login-btn"
                            onClick={() => navigate("/login")}
                            className="btn-press font-display border-2 border-[#0E0D0C] bg-[#D32F2F] px-3 py-2 text-sm tracking-widest text-[#F6F2E8] shadow-[3px_3px_0px_#0E0D0C] sm:px-5 sm:text-base"
                        >
                            DRIVER LOGIN
                        </button>
                    )}
                    <button
                        data-testid="nav-fleets-btn"
                        onClick={() => navigate("/fleet")}
                        className="btn-press font-display border-2 border-[#0E0D0C] bg-[#0055A5] px-3 py-2 text-sm tracking-widest text-[#F6F2E8] shadow-[3px_3px_0px_#0E0D0C] sm:px-5 sm:text-base"
                    >
                        FLEETS
                    </button>
                    <button
                        data-testid="nav-fleet-code-btn"
                        onClick={() => navigate("/access")}
                        className="btn-press font-display border-2 border-[#0E0D0C] bg-[#E5A93C] px-3 py-2 text-sm tracking-widest text-[#161413] shadow-[3px_3px_0px_#0E0D0C] sm:px-5 sm:text-base"
                    >
                        FLEET CODE
                    </button>
                </div>
            </div>
        </header>
    );
}
